<template>
  <div ref="NeyslaModelerUsage" class="transparent">
    <h2>{{ $t("portfolio.neysla.modelerUsage.pageLabel") }}</h2>
    <hr class="secondary" />
    <p
      v-for="(paragraph, i) in $t('portfolio.neysla.modelerUsage.paragraph')"
      v-html="
        replaceTextClass(
          replaceTextClass(paragraph, '{href}', 'https://github.com/restfulapi/api-strategy'),
          '{purple}',
          'text-white-purple'
        )
      "
      :key="`portfolioNeyslaModelerUsageParagraph-${i}`"
    />

    <h3>{{ $t("portfolio.neysla.modelerUsage.initialization.title") }}</h3>
    <p>{{ $t("portfolio.neysla.modelerUsage.initialization.paragraphA") }}</p>
    <ul>
      <template v-for="(elements, i) in $t('portfolio.neysla.modelerUsage.initialization.list')">
        <li
          v-html="replaceTextClass(elements.paragraph, '{purple}', 'text-white-purple')"
          :key="`elements-initialization-${i}`"
        />
        <ul :key="`list-${i}`">
          <template v-for="(elements2, j) in elements.list">
            <li
              v-html="replaceTextClass(elements2.paragraph, '{purple}', 'text-white-purple')"
              :key="`elements-initialization-${i}-${j}`"
            />
            <ul :key="`list-${i}-${j}`">
              <template v-for="(elements3, k) in elements2.list">
                <li
                  v-html="replaceTextClass(elements3, '{purple}', 'text-white-purple')"
                  :key="`elements-initialization-${i}-${j}-${k}`"
                />
              </template>
            </ul>
          </template>
        </ul>
      </template>
    </ul>
    <p
      v-html="
        $t('portfolio.neysla.modelerUsage.initialization.paragraphB', {
          purple: 'text-white-purple'
        })
      "
    ></p>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  const neysla = new Neysla();

  neysla.init({
    name: 'myApiModeler',
    url: 'https://www.my-domain.com/api/',
    headers: {
      'Authorization': 'Basic asodug2312pu312pu3_asodq231'
    }
  });
...
      </code>
    </pre>
    <p
      v-html="
        $t('portfolio.neysla.modelerUsage.initialization.paragraphC', {
          purple: 'text-white-purple'
        })
      "
    ></p>
    <datatable :title="initMethodTitle" :data="initMethodData"></datatable>
    <p>
      *
      <small>
        <a
          href="https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/responseType"
          class="general-anchor decorated"
          rel="noreferrer,noopener"
          target="_blank"
        >
          <span>{{ $t("portfolio.neysla.ctaSupportedResponse") }}</span> </a
        >.
      </small>
    </p>

    <h3>{{ $t("portfolio.neysla.modelerUsage.definition.title") }}</h3>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  let user = null,
  userContact = null,
  userContactPhone = null;
  const neysla = new Neysla();

  neysla.init({
    name: "myApiModeler",
    url: "https://www.my-domain.com/api/",
    headers: {
      'Authorization': 'Basic asodug2312pu312pu3_asodq231'
    },
    params: {
      foo: "bar"
    }
  }).then(modelers => {
    user = modelers.myApiModeler.setModel("user");
    userContact = modelers.myApiModeler.setModel([ "user", "contact" ]);
    userContactPhone = modelers.myApiModeler.setModel([ "user", "contact", "phone" ]);
  }).catch(error => console.log(error));
...
      </code>
    </pre>
    <p
      v-for="(paragraph, i) in $t('portfolio.neysla.modelerUsage.definition.paragraphA')"
      v-html="replaceTextClass(paragraph, '{purple}', 'text-white-purple')"
      :key="`portfolioNeyslaModelerUsageDefinitionParagraphA-${i}`"
    />
    <ul>
      <li
        v-for="(elements, i) in $t('portfolio.neysla.modelerUsage.definition.listA')"
        v-html="replaceTextClass(elements, '{purple}', 'text-white-purple')"
        :key="`elements-definition-a-${i}`"
      />
    </ul>
    <p
      v-for="(paragraph, i) in $t('portfolio.neysla.modelerUsage.definition.paragraphB')"
      v-html="replaceTextClass(paragraph, '{purple}', 'text-white-purple')"
      :key="`portfolioNeyslaModelerUsageDefinitionParagraphB-${i}`"
    />
    <ul>
      <li
        v-for="(elements, i) in $t('portfolio.neysla.modelerUsage.definition.listB')"
        v-html="replaceTextClass(elements, '{purple}', 'text-white-purple')"
        :key="`elements-definition-b-${i}`"
      />
    </ul>
    <p
      v-for="(paragraph, i) in $t('portfolio.neysla.modelerUsage.definition.paragraphC')"
      :key="`portfolioNeyslaModelerUsageDefinitionParagraphC-${i}`"
    >
      {{ paragraph }}
    </p>
    <datatable :title="modelerTitle" :data="modelerData"></datatable>

    <h3>{{ $t("portfolio.neysla.modelerUsage.requests.title") }}</h3>
    <p>
      {{ $t("portfolio.neysla.modelerUsage.requests.paragraphMain") }}
    </p>
    <ul>
      <li>GET</li>
      <li>HEAD</li>
      <li>POST</li>
      <li>PATCH</li>
      <li>PUT</li>
      <li>DELETE</li>
    </ul>

    <h4>GET</h4>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  user = modelers.yourApiModeler.setModel("user");

  user.get().then(data => console.log(data)).catch(error => console.log(error));

  user.get({
    delimiters: 10,
    responseType: "blob"
  }).then(data => console.log(data)).catch(error => console.log(error));

  user.get({
    params: {
      "order": "age",
      "foo": "notBar"
    }
  }).then(data => console.log(data)).catch(error => console.log(error));
...
      </code>
    </pre>
    <p
      v-html="
        $t('portfolio.neysla.modelerUsage.requests.get.paragraphA', { purple: 'text-white-purple' })
      "
    />
    <ul>
      <li
        v-for="(elements, i) in $t('portfolio.neysla.modelerUsage.requests.get.listA')"
        v-html="replaceTextClass(elements, '{purple}', 'text-white-purple')"
        :key="`elements-requests-get-a-${i}`"
      />
    </ul>
    <p>{{ $t("portfolio.neysla.modelerUsage.requests.get.paragraphB") }}</p>
    <ul>
      <li
        v-for="(elements, i) in $t('portfolio.neysla.modelerUsage.requests.get.listB')"
        v-html="replaceTextClass(elements, '{purple}', 'text-white-purple')"
        :key="`elements-requests-get-b-${i}`"
      />
    </ul>
    <p>{{ $t("portfolio.neysla.modelerUsage.requests.get.paragraphC") }}</p>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  userContactPhone = modelers.yourApiModeler.setModel([ "user", "contact", "phone" ]);

  userContactPhone.get({
    delimiters: [ 1, 500 ]
    params: {
      "page": "7",
      "perPage": "35"
    }
  }).then(data => console.log(data)).catch(error => console.log(error));
...
      </code>
    </pre>
    <p
      v-html="
        $t('portfolio.neysla.modelerUsage.requests.get.paragraphD', { purple: 'text-white-purple' })
      "
    />
    <p
      v-html="
        $t('portfolio.neysla.modelerUsage.requests.paragraphAttributes', {
          method: 'GET',
          purple: 'text-white-purple'
        })
      "
    />
    <datatable :title="modeledRequestTitle" :data="modeledRequestNoBodyData"></datatable>
    <p>
      *
      <small>
        <a
          href="https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/responseType"
          class="general-anchor decorated"
          rel="noreferrer,noopener"
          target="_blank"
        >
          <span>{{ $t("portfolio.neysla.ctaSupportedResponse") }}</span> </a
        >.
      </small>
    </p>

    <h4>HEAD</h4>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  user = modelers.yourApiModeler.setModel("user");

  user.head({
    delimiters: 10,
    params: {
      "order": "age",
      "name": "Ort"
    }
  }).then(data => console.log(data)).catch(error => console.log(error));
...
      </code>
    </pre>
    <p
      v-html="
        $t('portfolio.neysla.paragraphSameAttributes', {
          method: 'GET',
          purple: 'text-white-purple'
        })
      "
    />

    <h4>POST</h4>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  userContact = modelers.yourApiModeler.setModel([ "user", "contact" ]);

  userContact.post({
    delimiters: [ 12 ],
    requestType: "multipart",
    params: {
      "foo": "notBar"
    },
    body: {
      "address": "Saint street 177",
      "active": false,
      "first_name": "Roberto",
      "last_name": "Magallanez"
    }
  }).then(data => console.log(data)).catch(error => console.log(error));
...
      </code>
    </pre>
    <p
      v-html="
        $t('portfolio.neysla.modelerUsage.requests.post.paragraph', { purple: 'text-white-purple' })
      "
    />
    <p
      v-html="
        $t('portfolio.neysla.modelerUsage.requests.paragraphAttributes', {
          method: 'POST',
          purple: 'text-white-purple'
        })
      "
    />
    <datatable :title="modeledRequestTitle" :data="modeledRequestBodyData"></datatable>
    <p>
      *
      <small>
        <a
          href="https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/responseType"
          class="general-anchor decorated"
          rel="noreferrer,noopener"
          target="_blank"
        >
          <span>{{ $t("portfolio.neysla.ctaSupportedResponse") }}</span> </a
        >.
      </small>
    </p>

    <h4>PATCH</h4>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  userContact = modelers.yourApiModeler.setModel([ "user", "contact" ]);

  userContact.patch({
    delimiters: [ 12, 4 ],
    params: {
      "name": "Bart"
    },
    body: {
      "active": true
    }
  }).then(data => console.log(data)).catch(error => console.log(error));
...
      </code>
    </pre>
    <p
      v-html="
        $t('portfolio.neysla.modelerUsage.requests.patch.paragraph', {
          purple: 'text-white-purple'
        })
      "
    />
    <p
      v-html="
        $t('portfolio.neysla.paragraphSameAttributes', {
          method: 'POST',
          purple: 'text-white-purple'
        })
      "
    />

    <h4>PUT</h4>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  userContact = modelers.yourApiModeler.setModel([ "user", "contact" ]);

  userContact.put({
    delimiters: [ 12, 210 ],
    params: {
      "active": true
    }
  }).then(data => console.log(data)).catch(error => console.log(error));
...
      </code>
    </pre>
    <p
      v-html="
        $t('portfolio.neysla.paragraphSameAttributes', {
          method: 'POST',
          purple: 'text-white-purple'
        })
      "
    />

    <h4>DELETE</h4>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  userContact = modelers.yourApiModeler.setModel([ "user", "contact" ]);

  userContact.remove({
    delimiters: [ 12, 210 ],
    params: {
      "foo": "bar"
    }
  }).then(data => console.log(data)).catch(error => console.log(error));
...
      </code>
    </pre>
    <p
      v-html="
        $t('portfolio.neysla.paragraphSameAttributes', {
          method: 'POST',
          purple: 'text-white-purple'
        })
      "
    />

    <h4>{{ $t("portfolio.neysla.progress.title") }}</h4>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  const file = document.getElementById("myFile").files[0];
  userContact = modelers.yourApiModeler.setModel([ "user", "contact" ]);

  userContact.post({
    delimiters: [ 12, 210 ],
    params: {
      "foo": "bar"
    },
    body: {
      file,
      name: "My file"
    },
    progress(progressEv){
      // {{ $t('portfolio.neysla.progress.codeComment') }}
    }
  }).then(data => console.log(data)).catch(error => console.log(error));
...
      </code>
    </pre>
    <p>
      <span
        v-html="$t('portfolio.neysla.progress.paragraphA', { purple: 'text-white-purple' })"
      ></span>
      <a
        href="https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/progress_event"
        class="general-anchor decorated"
        rel="noreferrer,noopener"
        target="_blank"
      >
        <span>progress</span>
      </a>
      <span
        v-html="$t('portfolio.neysla.progress.paragraphB', { purple: 'text-white-purple' })"
      ></span>
    </p>

    <h3>{{ $t("portfolio.neysla.response.title") }}</h3>
    <p v-html="$t('portfolio.neysla.response.paragraph', { purple: 'text-white-purple' })"></p>
    <datatable :title="$props.responseTitle" :data="$props.responseData"></datatable>
    <p>
      *
      <small v-html="$t('portfolio.neysla.response.note', { purple: 'text-white-purple' })"></small>
    </p>

    <h3>{{ $t("portfolio.neysla.modelerUsage.multiple.title") }}</h3>
    <p
      v-for="(paragraph, i) in $t('portfolio.neysla.modelerUsage.multiple.paragraph')"
      v-html="replaceTextClass(paragraph, '{purple}', 'text-white-purple')"
      :key="`portfolioNeyslaModelerUsageMultipleParagraph-${i}`"
    />
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  let user = null,
  userContact = null;
  let store = null,
  storeElement = null;

  const neysla = new Neysla();

  neysla.init([
  {
    name: "myApiUserModeler",
    headers: {
      'Authorization': 'Basic asodug2312pu312pu3_asodq231'
    },
    url: "https://user.my-domain.com/api/"
  },
  {
    name: "myApiStoreModeler",
    url: "https://store.my-domain.com/api/"
  }
  ]).then(modelers => {

  user = modelers.myApiUserModeler.setModel("user");
  userContact = modelers.myApiUserModeler.setModel([ "user", "contact" ]);

  store = modelers.myApiStoreModeler.setModel("store");
  storeElement = modelers.myApiStoreModeler.setModel([ "store", "element" ]);

  }).catch(error => console.log(error));
...
      </code>
    </pre>
  </div>
</template>

<script>
const Datatable = () =>
  import(/* webpackChunkName: "Datatable" */ "@/components/Global/Datatable.vue");

export default {
  name: "NeyslaModelerUsage",
  components: {
    Datatable
  },
  props: {
    /*
     * @property - responseTitle
     * Sets response table titles
     */
    responseTitle: {
      type: Array,
      required: true
    },
    /*
     * @property - responseData
     * Sets response table data
     */
    responseData: {
      type: Array,
      required: true
    }
  },
  computed: {
    /*
     * @computed - initMethodTitle
     * Adds table header values to direct init method table
     */
    initMethodTitle() {
      return [
        {
          name: "attribute",
          displayName: this.$t(
            "portfolio.neysla.modelerUsage.initMethodTable.title.columnAttribute"
          )
        },
        {
          name: "description",
          displayName: this.$t(
            "portfolio.neysla.modelerUsage.initMethodTable.title.columnDescription"
          )
        },
        {
          name: "isRequired",
          displayName: this.$t(
            "portfolio.neysla.modelerUsage.initMethodTable.title.columnIsRequired"
          )
        },
        {
          name: "default",
          displayName: this.$t("portfolio.neysla.modelerUsage.initMethodTable.title.columnDefault")
        },
        {
          name: "example",
          displayName: this.$t("portfolio.neysla.modelerUsage.initMethodTable.title.columnExample")
        }
      ];
    },
    /*
     * @computed - initMethodData
     * Adds table data values to init method data table
     */
    initMethodData() {
      return [
        {
          attribute: "name",
          description: this.$t("portfolio.neysla.modelerUsage.initMethodTable.data.rowName"),
          isRequired: this.$t("portfolio.neysla.modelerUsage.initMethodTable.data.isRequired.yes"),
          default: "undefined",
          example: "myApiModeler"
        },
        {
          attribute: "url",
          description: this.$t("portfolio.neysla.modelerUsage.initMethodTable.data.rowUrl"),
          isRequired: this.$t("portfolio.neysla.modelerUsage.initMethodTable.data.isRequired.yes"),
          default: "undefined",
          example: "https://www.my-domain.com/api/"
        },
        {
          attribute: "headers",
          description: this.$t("portfolio.neysla.modelerUsage.initMethodTable.data.rowHeaders"),
          isRequired: this.$t("portfolio.neysla.modelerUsage.initMethodTable.data.isRequired.no"),
          default: "undefined",
          example: '{ "Authorization": "Basic asodug2312pu312pu3_asodq231" }'
        },
        {
          attribute: "body",
          description: this.$t("portfolio.neysla.modelerUsage.initMethodTable.data.rowBody"),
          isRequired: this.$t("portfolio.neysla.modelerUsage.initMethodTable.data.isRequired.no"),
          default: "undefined",
          example: '{ "foo": "bar" }'
        },
        {
          attribute: "params",
          description: this.$t("portfolio.neysla.modelerUsage.initMethodTable.data.rowParams"),
          isRequired: this.$t("portfolio.neysla.modelerUsage.initMethodTable.data.isRequired.no"),
          default: "undefined",
          example: '{ "myqueryparam": "bar" }'
        },
        {
          attribute: "requestType",
          description: this.$t("portfolio.neysla.modelerUsage.initMethodTable.data.rowRequestType"),
          isRequired: this.$t("portfolio.neysla.modelerUsage.initMethodTable.data.isRequired.no"),
          default: "urlencoded",
          example: "json"
        },
        {
          attribute: "responseType*",
          description: this.$t(
            "portfolio.neysla.modelerUsage.initMethodTable.data.rowResponseType"
          ),
          isRequired: this.$t("portfolio.neysla.modelerUsage.initMethodTable.data.isRequired.no"),
          default: "json",
          example: "text"
        }
      ];
    },
    /*
     * @computed - modelerTitle
     * Adds table header values to modeler data table
     */
    modelerTitle() {
      return [
        {
          name: "name",
          displayName: this.$t("portfolio.neysla.modelerUsage.modelerTable.title.columnName")
        },
        {
          name: "description",
          displayName: this.$t("portfolio.neysla.modelerUsage.modelerTable.title.columnDescription")
        },
        {
          name: "argumentsType",
          displayName: this.$t(
            "portfolio.neysla.modelerUsage.modelerTable.title.columnArgumentsType"
          )
        }
      ];
    },
    /*
     * @computed - modelerData
     * Adds table data values to modeler data table
     */
    modelerData() {
      return [
        {
          name: "setModel",
          description: this.$t("portfolio.neysla.modelerUsage.modelerTable.data.rowSetModel"),
          argumentsType: "array | string"
        }
      ];
    },
    /*
     * @computed - modeledRequestTitle
     * Adds table header values to modeler data table
     */
    modeledRequestTitle() {
      return [
        {
          name: "attribute",
          displayName: this.$t(
            "portfolio.neysla.modelerUsage.modeledRequestTable.title.columnAttribute"
          )
        },
        {
          name: "description",
          displayName: this.$t(
            "portfolio.neysla.modelerUsage.modeledRequestTable.title.columnDescription"
          )
        },
        {
          name: "default",
          displayName: this.$t(
            "portfolio.neysla.modelerUsage.modeledRequestTable.title.columnDefault"
          )
        },
        {
          name: "datatype",
          displayName: this.$t(
            "portfolio.neysla.modelerUsage.modeledRequestTable.title.columnDatatype"
          )
        }
      ];
    },
    /*
     * @computed - modeledRequestBodyData
     * Adds table data values to modeled request data table
     */
    modeledRequestNoBodyData() {
      return [
        {
          attribute: "headers",
          description: this.$t("portfolio.neysla.modelerUsage.modeledRequestTable.data.rowHeaders"),
          default: "undefined",
          datatype: "object"
        },
        {
          attribute: "delimiters",
          description: this.$t(
            "portfolio.neysla.modelerUsage.modeledRequestTable.data.rowDelimiters"
          ),
          default: "undefined",
          datatype: "array | integer | string"
        },
        {
          attribute: "params",
          description: this.$t("portfolio.neysla.modelerUsage.modeledRequestTable.data.rowParams"),
          default: "undefined",
          datatype: "object"
        },
        {
          attribute: "requestType",
          description: this.$t(
            "portfolio.neysla.modelerUsage.modeledRequestTable.data.rowRequestType"
          ),
          default: "urlencoded",
          datatype: "string"
        },
        {
          attribute: "responseType*",
          description: this.$t(
            "portfolio.neysla.modelerUsage.modeledRequestTable.data.rowResponseType"
          ),
          default: "json",
          datatype: "string"
        }
      ];
    },
    /*
     * @computed - modeledRequestBodyData
     * Adds body values to modeled request data table with no body
     */
    modeledRequestBodyData() {
      return [
        ...this.modeledRequestNoBodyData,
        {
          attribute: "body",
          description: this.$t("portfolio.neysla.modelerUsage.modeledRequestTable.data.rowBody"),
          default: "undefined",
          datatype: "object"
        }
      ];
    }
  }
};
</script>
